import type { AppAlert, AppAlertKind } from './types'

/** One-line headline for a live alert, shared by the toasts and the bell panel. */
export function alertText(kind: AppAlertKind): string {
  switch (kind) {
    case 'live_vouch':
      return 'passed a Live Vouch'
    case 'verify_completed':
      return 'completed a verification step'
    case 'signal_verified':
      return 'added a new verified proof'
    case 'tier_up':
      return 'reached a higher trust tier'
    case 'announcement':
      // Announcements render their own title in the bell, not "<name> <verb>".
      return ''
  }
}

// ── Persistence ───────────────────────────────────────────────────────────
// Alerts live in React state but are mirrored to localStorage, keyed per
// recruiter, so closing the tab (or a reload) doesn't lose the bell's history.
// PII-free: only the candidate's display name + opaque id, which the recruiter
// already sees in the console. Capped to match the in-memory ceiling.

const KEY_PREFIX = 'vouchr.alerts.'
const MAX_PERSISTED = 30

const keyFor = (recruiterId: string) => `${KEY_PREFIX}${recruiterId}`

/** Read the persisted alert history for a recruiter (newest first). */
export function loadAlerts(recruiterId: string | undefined | null): AppAlert[] {
  if (!recruiterId || typeof localStorage === 'undefined') return []
  try {
    const raw = localStorage.getItem(keyFor(recruiterId))
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed as AppAlert[]).slice(0, MAX_PERSISTED) : []
  } catch {
    return []
  }
}

/** Persist the alert history for a recruiter. Best-effort (ignores quota / private mode). */
export function saveAlerts(recruiterId: string | undefined | null, alerts: AppAlert[]): void {
  if (!recruiterId || typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(keyFor(recruiterId), JSON.stringify(alerts.slice(0, MAX_PERSISTED)))
  } catch {
    /* quota exceeded / private mode — alerts simply stay in-memory this session */
  }
}
